"use client";
import { useAnalytics } from "@areahub/adapters/src/analytics/analytics.client";
import {
  Box,
  Center,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react";
import { ReportRedirectModal } from "@ui/components/modal";
import { lazy, Suspense, useContext } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { PlaceSearchContext, RedirectModalContext } from "./contexts";
import { PlaceSearchInput } from "./input";
import LoadingSearchOption from "./search-option/loading";
import { LoadingArea } from "@ui/loading";

const PlaceSearchOption = lazy(() => import("./search-option/place"));

const CurrentLocationSearchOption = lazy(
  () => import("./search-option/current-location"),
);

export function PlaceSearchModal() {
  const analytics = useAnalytics();
  const placeSearchCtx = useContext(PlaceSearchContext);

  // Get the state of the redirect modal so we can render it
  const redirectModal = useContext(RedirectModalContext);
  const {
    isOpen: redirectModalIsOpen,
    onClose: redirectModalOnClose,
    runCallback: redirectModalRunCallback,
  } = redirectModal;

  const showUseLocation = placeSearchCtx
    ? placeSearchCtx?.results?.length === 0
    : false;

  return (
    <Suspense fallback={<LoadingArea h="100vh" w="full" />}>
      <>
        {redirectModal ? (
          <ReportRedirectModal
            isOpen={redirectModalIsOpen}
            onClose={redirectModalOnClose}
            onFreeClick={() => {
              analytics.track("user clicked free report", {
                ui: "ReportRedirectModal",
                parent: "PlaceSearchModal",
              });
              redirectModalRunCallback();
            }}
            onPremiumClick={() => {
              analytics.track("user clicked premium cta", {
                ui: "ReportRedirectModal",
                parent: "PlaceSearchModal",
              });
            }}
          />
        ) : (
          <></>
        )}
        {placeSearchCtx ? (
          <Modal
            scrollBehavior="inside"
            isOpen={placeSearchCtx?.modal?.isOpen}
            onClose={placeSearchCtx?.modal?.onClose}
          >
            <ModalOverlay />
            <ModalContent
              data-test="search-places-modal"
              role="combobox"
              aria-expanded="true"
              aria-haspopup="listbox"
              borderRadius="lg"
              overflow="hidden"
              top="2vh"
              bg="transparent"
              shadow="lg"
              maxW="2xl"
              mx={{
                sm: "2",
                base: "4",
              }}
            >
              <Flex pos="relative" align="stretch" borderBottomColor="gray.100">
                <PlaceSearchInput
                  borderBottomRadius={
                    placeSearchCtx.results || showUseLocation ? 0 : "md"
                  }
                  onChange={(e) => {
                    placeSearchCtx.setQuery(e.target.value);
                  }}
                  value={placeSearchCtx.query}
                  onKeyDown={placeSearchCtx.onKeyDown}
                />
                <Center pos="absolute" right={7} h="68px">
                  <Icon as={IoSearchOutline} color="blue.500" boxSize="20px" />
                </Center>
              </Flex>
              <ModalBody maxH="66vh" p="0" ref={placeSearchCtx.resultMenuRef}>
                {
                  <Box
                    sx={{
                      px: 4,
                      bg: "white",
                      borderRadius: "md",
                      borderTopRadius: 0,
                      ".areahub-dark &": { bg: "gray.700" },
                    }}
                  >
                    <Box
                      as="ul"
                      role="listbox"
                      borderTopWidth="1px"
                      pt={2}
                      pb={4}
                    >
                      {showUseLocation ? (
                        <Suspense
                          key={"use-current-location-option"}
                          fallback={
                            <LoadingSearchOption
                              as="li"
                              label="Use your location"
                            />
                          }
                        >
                          {placeSearchCtx ? (
                            <CurrentLocationSearchOption
                              key={"use-current-location-option"}
                              as="li"
                              label={"Use your location"}
                              data-testid="current-location-search-option"
                              {...{
                                router: placeSearchCtx?.router,
                                rsc: placeSearchCtx?.rsc,
                                modal: placeSearchCtx?.modal,
                              }}
                            />
                          ) : (
                            <></>
                          )}
                        </Suspense>
                      ) : (
                        <></>
                      )}
                      {!showUseLocation &&
                        placeSearchCtx.results?.length > 0 &&
                        placeSearchCtx.results.map((result, index: number) => {
                          const selected = index === placeSearchCtx.active;
                          return (
                            <Suspense
                              key={index}
                              fallback={
                                <LoadingSearchOption
                                  as="li"
                                  label="Loading Results"
                                  role="option"
                                />
                              }
                            >
                              <PlaceSearchOption
                                ref={(element) => {
                                  placeSearchCtx.resultRefs.current[index] =
                                    element;
                                }}
                                as="li"
                                key={index}
                                id={`search-item-${index}`}
                                aria-selected={selected ? true : false}
                                cursor="pointer"
                                onMouseEnter={() => {
                                  placeSearchCtx.onMouseSetActive(index);
                                }}
                                onClick={placeSearchCtx.redirectResult}
                                role="option"
                                label={result.address.label}
                              />
                            </Suspense>
                          );
                        })}
                    </Box>
                  </Box>
                }
              </ModalBody>
            </ModalContent>
          </Modal>
        ) : (
          <></>
        )}
      </>
    </Suspense>
  );
}

export default PlaceSearchModal;
